import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { HighlightIndices, Option } from '@rivethealth/data/options';
import Fuse, { FuseResult } from 'fuse.js';
import { debounce } from 'lodash';
import { AnchoredPosition } from '../callout/callout-position';
import '../fuse-worker/init';
import { Event, State, Update } from './autocomplete';
import { RecentOptionsService } from './recent-dropdown-options.service';
import { DeprecatedTextFieldComponent } from './text-field.component';

type Highlighted =
  | {
      type: 'searchTerm';
      index?: never;
    }
  | {
      type: 'recent';
      index: number;
    }
  | {
      type: 'results';
      index: number;
    };

enum Key {
  ARROW_DOWN = 'ArrowDown',
  ARROW_UP = 'ArrowUp',
  ESCAPE = 'Escape',
  IE_ESCAPE = 'Esc',
  ENTER = 'Enter',
  TAB = 'Tab',
}

export class AutoCompleteSearchWorker {
  constructor() {
    this.worker = (window as any).createFuseJSWorker();
  }

  worker: Worker;

  setSearchOptions(options: Option[]) {
    this.worker.postMessage({
      type: 'setSearchOptions',
      options,
    });
  }

  search(term: string): Promise<
    {
      item: Option;
      titleHighlightIndices: HighlightIndices;
      subtitleHighlightIndices: HighlightIndices;
    }[]
  > {
    return new Promise(resolve => {
      this.worker.onmessage = (
        ev: MessageEvent<{ matches: FuseResult<Option>[] }>,
      ) => {
        const matches = (ev.data.matches || []).map(({ item, matches }) => ({
          item,
          titleHighlightIndices:
            matches
              ?.find(m => m.key == 'title')
              ?.indices.map<HighlightIndices[number]>(([a, b]) => [a, b]) || [],
          subtitleHighlightIndices:
            matches
              ?.find(m => m.key == 'subtitle')
              ?.indices.map<HighlightIndices[number]>(([a, b]) => [a, b]) || [],
        }));
        resolve(matches);
      };
      this.worker.postMessage({ type: 'search', term });
    });
  }
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'autocomplete-textbox',
  templateUrl: './autocomplete-textbox.component.html',
  styleUrls: ['./autocomplete-textbox.component.scss'],
})
export class AutoCompleteTextboxComponent implements OnChanges {
  constructor(
    private readonly ref: ElementRef,
    private readonly cdr: ChangeDetectorRef,
    private readonly recentOptionsService: RecentOptionsService,
  ) {}

  @Input('case') case: string;
  @Input('disabled') disabled: boolean;
  @Input('error') error: boolean;
  @Input('help') help: string;
  @Input('label') label: string;
  @Input() combinedRight: boolean = false;
  @Input() combinedLeft: boolean = false;
  @Input('name') name: string;
  @Input('textFieldValue') textFieldValue: string;
  @Input('placeholder') placeholder: string = '';
  @Input('required') required: boolean;
  @Input('warning') warning: boolean;
  @Input('preferredPosition') preferredPosition = AnchoredPosition.BOTTOM_RIGHT;
  @Input('allowedPositions') allowedPositions = [
    AnchoredPosition.TOP_LEFT,
    AnchoredPosition.TOP_CENTER,
    AnchoredPosition.TOP_RIGHT,
    AnchoredPosition.BOTTOM_RIGHT,
    AnchoredPosition.BOTTOM_CENTER,
    AnchoredPosition.BOTTOM_LEFT,
  ];
  @Input('searchOptions') searchOptions: Option[];
  @Input('recentIdentifier') recentIdentifier: string;
  @Input('resultLabel') resultLabel = 'Results';
  @Input('maxHeight') maxHeight: number = 400;
  @Output('textFieldValueChange')
  textFieldValueChange = new EventEmitter<string>();

  @ViewChild('textField', { static: false })
  textFieldEl: DeprecatedTextFieldComponent;

  searchTerm = '';
  state: State = 'Closed';
  recentIds: string[] = [];
  matches: {
    item: Option;
    titleHighlightIndices: HighlightIndices;
    subtitleHighlightIndices: HighlightIndices;
  }[] = [];
  recentMatches: {
    item: Option;
    titleHighlightIndices: HighlightIndices;
    subtitleHighlightIndices: HighlightIndices;
  }[] = [];
  recentSearchFunction: (term: string) => FuseResult<Option>[] = () => [];
  highlighted: Highlighted = { type: 'searchTerm' };

  // what text should render in the textbox
  // we will render the current search term
  // or the highlighted option if the user
  // is using keyboard arrows or is hovering
  // over an option
  get displayText() {
    switch (this.highlighted.type) {
      case 'searchTerm':
        return this.searchTerm;
      case 'recent':
        return this.recentMatches[this.highlighted.index].item.id;
      case 'results':
        return this.matches[this.highlighted.index].item.id;
    }
  }

  get isVisible() {
    return (
      this.state != 'Closed' &&
      (this.searchTerm != '' || this.recentMatches.length > 0)
    );
  }

  get isSearching() {
    return (
      this.state == 'SearchRequested' ||
      this.state == 'Searching' ||
      this.state == 'SearchingSearchRequested'
    );
  }

  get searchOptionWidth() {
    return this.ref.nativeElement.offsetWidth + 'px';
  }

  searchRecent = () => {
    this.recentMatches = this.recentSearchFunction(this.searchTerm)
      .map(({ item, matches }) => ({
        item,
        titleHighlightIndices:
          matches
            ?.find(m => m.key == 'title')
            ?.indices.map<HighlightIndices[number]>(([a, b]) => [a, b]) || [],
        subtitleHighlightIndices:
          matches
            ?.find(m => m.key == 'subtitle')
            ?.indices.map<HighlightIndices[number]>(([a, b]) => [a, b]) || [],
      }))
      .slice(0, 5);
  };

  searchRecentDebounced = debounce(this.searchRecent, 100);

  search = () => {
    this.worker.search(this.searchTerm).then(matches => {
      this.matches = matches.filter(
        m => !this.recentIds.includes(m.item.id.toString()),
      );
      this.handleEvent('CompleteSearch');
    });
    this.searchRecent();
    this.handleEvent('StartSearch');
  };

  searchDebounced = debounce(this.search, 200);

  trackByFn(index: number) {
    return index;
  }

  setSearchTerm(term: string) {
    this.searchTerm = term;
    this.highlighted = { type: 'searchTerm' };
    if (term == '') {
      this.handleEvent('ClearSearchTerm');
      this.handleRecentChange();
    } else {
      this.handleEvent('SetSearchTerm');
    }
  }

  focus() {
    this.handleEvent('Focus');
  }

  @HostListener('keyup', ['$event'])
  @HostListener('keydown', ['$event'])
  keyPress(event: KeyboardEvent) {
    // don't mess with keyboard events if the search isn't visible
    if (!this.isVisible) {
      return;
    }
    // these are the events we will handle
    switch (event.key) {
      case Key.ARROW_DOWN:
      case Key.ARROW_UP:
      case Key.TAB:
      case Key.ENTER:
      case Key.ESCAPE:
      case Key.IE_ESCAPE:
        break;
      default:
        return;
    }
    event.stopPropagation();
    event.preventDefault();
    // but we will only handle them on keydown
    if (event.type == 'keyup') {
      return;
    }

    switch (event.key) {
      case Key.ARROW_DOWN:
        this.handleArrows(false);
        break;
      case Key.ARROW_UP:
        this.handleArrows(true);
        break;
      case Key.ESCAPE:
      case Key.IE_ESCAPE:
        this.selectSearchTerm();
        break;
      case Key.TAB:
      case Key.ENTER:
        switch (this.highlighted.type) {
          case 'searchTerm':
            this.selectSearchTerm();
            break;
          case 'recent':
            this.selectOption(this.recentMatches[this.highlighted.index].item);
            break;
          case 'results':
            this.selectOption(this.matches[this.highlighted.index].item);
            break;
        }
        break;
    }
  }

  handleArrows(up: boolean) {
    if (!up) {
      switch (this.highlighted.type) {
        case 'searchTerm':
          if (this.recentMatches.length > 0) {
            this.highlighted = { type: 'recent', index: 0 };
          } else if (this.matches.length > 0) {
            this.highlighted = { type: 'results', index: 0 };
          }
          break;
        case 'recent':
          if (this.recentMatches.length - 1 > this.highlighted.index) {
            this.highlighted.index++;
          } else if (this.matches.length > 0) {
            this.highlighted = { type: 'results', index: 0 };
          }
          break;
        case 'results':
          if (this.matches.length - 1 > this.highlighted.index) {
            this.highlighted.index++;
          }
      }
    } else {
      switch (this.highlighted.type) {
        case 'recent':
          if (this.highlighted.index > 0) {
            this.highlighted.index--;
          } else if (this.searchTerm != '') {
            this.highlighted = { type: 'searchTerm' };
          }
          break;
        case 'results':
          if (this.highlighted.index > 0) {
            this.highlighted.index--;
          } else if (this.recentMatches.length > 0) {
            this.highlighted = {
              type: 'recent',
              index: this.recentMatches.length - 1,
            };
          } else {
            this.highlighted = { type: 'searchTerm' };
          }
      }
    }
  }

  selectOption(o: Option) {
    const id = o.id.toString();
    this.searchTerm = id;
    this.selectSearchTerm();
  }

  selectSearchTerm() {
    this.textFieldValueChange.emit(this.searchTerm);
    if (this.recentIdentifier) {
      this.recentIds = this.recentOptionsService.enqueue(
        this.searchTerm,
        this.recentIdentifier,
      );
      this.handleRecentChange();
    }
    this.highlighted = { type: 'searchTerm' };
    this.textFieldEl.focus();
    this.handleEvent('SelectOption');
  }

  isHighlighted(type: Highlighted['type'], index?: number) {
    return this.highlighted.type == type && this.highlighted.index == index;
  }

  highlightOption(h: Highlighted) {
    this.highlighted = h;
    this.cdr.markForCheck();
  }

  handleEvent(e: Event) {
    const { state, action } = Update(this.state, e);
    this.state = state;
    switch (action) {
      case 'SearchDebounced':
        this.searchDebounced();
        break;
      case 'SearchImmediate':
        this.search();
        break;
      case 'FastSearchDebounced':
        this.searchRecentDebounced();
        break;
    }
    this.cdr.markForCheck();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['searchOptions']) {
      this.worker.setSearchOptions(this.searchOptions);
      this.handleRecentChange();
    }
    if (changes['textFieldValue']) {
      this.searchTerm = this.textFieldValue;
    }
    if (changes['recentIdentifier']) {
      this.recentIds =
        this.recentOptionsService.getLocalLimited(this.recentIdentifier) || [];
      this.handleRecentChange();
    }
  }

  private handleRecentChange() {
    const recentOptions = this.searchOptions.filter(o =>
      this.recentIds.includes(o.id.toString()),
    );
    if (this.searchTerm == '') {
      this.recentMatches = recentOptions
        .map(item => ({
          item,
          subtitleHighlightIndices: [],
          titleHighlightIndices: [],
        }))
        .slice(0, 5);
    }
    const fuse = new Fuse(recentOptions, {
      keys: ['subtitle', 'title'],
      includeMatches: true,
      threshold: 0.6,
    });
    this.recentSearchFunction = (term: string) => fuse.search(term);
  }

  @Input('worker') _worker: AutoCompleteSearchWorker;

  get worker() {
    if (this._worker == null) {
      this._worker = new AutoCompleteSearchWorker();
    }
    return this._worker;
  }
}
